/**
 * **„Nowy pomiar"** — KANON `pomiary-klienta-2026-09-18`, `02-szuflada-nowy-pomiar`.
 *
 * The form used to stand permanently on the tab. Owner, 18 September 2026: "trener przy biurku
 * prawie nigdy nie wpisuje pomiaru. Dodawanie zostaje możliwe, ale ciche." So it is a drawer off
 * the right edge, opened by one quiet secondary button beside the quantity switch.
 *
 * ── Base UI supplies the behaviour and we supply every visual byte ───────────────────────────
 * Focus trap, `aria-modal`, Escape, the press outside and the focus RETURN are the Dialog's. The
 * trigger is rendered here rather than in the tab precisely so the return lands on the „Pomiar"
 * button the trainer pressed, with no `finalFocus` bookkeeping of our own — the shape
 * `ProgressionSheet` had to work around only because its closes are routed through a prompt.
 *
 * ── No date field, and nine fields ───────────────────────────────────────────────────────────
 * `POST /customer/{id}/body` stamps the moment server-side and the backend would discard a date
 * this form sent. The nine are the nine MOBILE writes; `shoulderCm` and `forearmCm` stay readable
 * on the chart and in the table and cannot be typed, because mobile cannot type them either.
 *
 * ── It is usable on a short window ───────────────────────────────────────────────────────────
 * The fields scroll and the footer does not: at 1280×620 the nine inputs are taller than the
 * viewport, and a primary that has scrolled off the bottom of a fixed panel is the one outcome the
 * fit-or-scroll rule calls worse than either.
 *
 * ── A REFUSAL puts the trainer on the field that refused ─────────────────────────────────────
 * It is a real `<form>`, so Enter saves from any field, and a refused submit moves focus to the
 * first invalid input — which scrolls it back into view. On the same 620px window the refused field
 * was usually above the fold with focus left sitting on the button, so the panel said no and showed
 * nothing. The count beside the footer is the other half: how many fields, said once, in an alert.
 *
 * ── Closing without saving keeps what was typed ──────────────────────────────────────────────
 * Escape is one keystroke from every field here and `useMeasurements` deliberately exposes no
 * reset: reopening finds the numbers still there. Only a SUCCESSFUL save clears them, because that
 * is the one moment they have been spent.
 */

import { Dialog } from '@base-ui/react/dialog';
import { Fragment, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import ghost from '~/shared/ui/ghostButton.module.css';
import ink from '~/shared/ui/inkButton.module.css';

import type { MeasurementsState } from '../lib/useMeasurements';
import {
  MEASUREMENT_FORM_FIELDS,
  measurementBounds,
  measurementFieldLabelKey,
  measurementFieldUnitKey,
  type MeasurementFormField,
} from '../model/measurements';

import styles from './MeasurementDrawer.module.css';

const KEY = 'web:clientDetail.body';

/** Where the canon breaks the nine: mass above the rule, girths below it. */
const FIRST_GIRTH: MeasurementFormField = 'neck';

export interface MeasurementDrawerProps {
  readonly body: MeasurementsState;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** One class for the trigger's geometry, so the button matches the row it stands in. */
  readonly triggerClassName: string;
}

export function MeasurementDrawer({
  body,
  open,
  onOpenChange,
  triggerClassName,
}: MeasurementDrawerProps) {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);

  /**
   * Hand the trainer the field that refused.
   *
   * Reached through the form's own `elements` rather than through nine refs: the inputs already
   * carry a `name`, which is what a form element is looked up by, and nine refs would be nine more
   * things to keep in step with `MEASUREMENT_FORM_FIELDS`.
   */
  const focusFirstInvalid = (fields: readonly MeasurementFormField[]) => {
    const first = fields[0];
    if (first === undefined) return;
    const input = formRef.current?.elements.namedItem(first);
    if (input instanceof HTMLInputElement) {
      input.scrollIntoView({ block: 'nearest' });
      input.focus();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {/* The canon's plus, and it is DRAWN — an inline SVG like every other icon in this repo,
        * never a `+` character (which is type and scales with the font) and never an emoji. Without
        * it the trigger read as a twelfth quantity chip in the row it stands in. The label carries
        * the verb the two-word caption cannot. */}
      <Dialog.Trigger
        aria-label={t(`${KEY}.addButtonLabel`)}
        className={`${String(ghost.ghost)} ${triggerClassName}`}
      >
        {/* The canon's 13px plus, at its own 1.6px stroke. */}
        <svg aria-hidden="true" height="13" viewBox="0 0 13 13" width="13">
          <path
            d="M6.5 1.5v10M1.5 6.5h10"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="1.6"
          />
        </svg>
        {t(`${KEY}.addButton`)}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Backdrop className={styles.scrim} />
        <Dialog.Popup className={styles.drawer}>
          <div className={styles.head}>
            <Dialog.Title className={styles.title}>
              {t('clients.detail.body.addTitle')}
            </Dialog.Title>
            <Dialog.Close
              aria-label={t(`${KEY}.drawerClose`)}
              className={styles.close}
            >
              <svg aria-hidden="true" height="12" viewBox="0 0 12 12" width="12">
                <path
                  d="M2 2l8 8M10 2l-8 8"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth="1.6"
                />
              </svg>
            </Dialog.Close>
          </div>

          <form
            className={styles.form}
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              focusFirstInvalid(
                body.submit(() => {
                  onOpenChange(false);
                }),
              );
            }}
            ref={formRef}
          >
            <div className={styles.scroll}>
              <Dialog.Description className={styles.intro}>
                {t(`${KEY}.drawerIntro`)}
              </Dialog.Description>

              <div className={styles.grid}>
                {MEASUREMENT_FORM_FIELDS.map((field) => {
                  const required = field === 'weight';
                  const invalid = body.invalid.includes(field);
                  const bounds = measurementBounds(field);
                  return (
                    <Fragment key={field}>
                      {/* The canon's one rule inside the form, and it spans both columns. */}
                      {field === FIRST_GIRTH ? (
                        <h3 className={styles.groupTitle}>{t(`${KEY}.drawerGirths`)}</h3>
                      ) : null}
                      <label className={styles.field}>
                        <span
                          className={`${String(styles.label)} ${required ? String(styles.labelRequired) : ''}`}
                        >
                          {t(measurementFieldLabelKey(field))}
                          {/* The unit rides in the LABEL rather than as an adornment inside the box,
                            * so it reaches a screen reader as part of the field's own name. */}
                          <em className={styles.unit}>{t(measurementFieldUnitKey(field))}</em>
                        </span>
                        <input
                          aria-describedby={invalid ? `measure-${field}-invalid` : undefined}
                          aria-invalid={invalid || undefined}
                          className={`${String(styles.input)} ${invalid ? String(styles.inputInvalid) : ''}`}
                          inputMode="decimal"
                          name={field}
                          onChange={(event) => {
                            body.setField(field, event.target.value);
                          }}
                          required={required}
                          type="text"
                          value={body.values[field]}
                        />
                        {invalid ? (
                          <em className={styles.fieldError} id={`measure-${field}-invalid`}>
                            {/* The bound the VALIDATOR enforces, per field kind, straight out of
                              * `model/measurements`. "Wartość poza zakresem" named no range at all,
                              * and the three kinds here are 20 to 500 kg, 0 to 80% and 5 to 300 cm. */}
                            {t(`${KEY}.invalidRange`, {
                              min: bounds.min,
                              max: bounds.max,
                              unit: t(measurementFieldUnitKey(field)),
                            })}
                          </em>
                        ) : null}
                      </label>
                    </Fragment>
                  );
                })}
              </div>

              {body.saveFailed ? (
                <p className={styles.error} role="alert">
                  {t('clients.detail.body.saveError')}
                </p>
              ) : null}
            </div>

            <div className={styles.foot}>
              {/* ALWAYS MOUNTED and empty when nothing refused: an alert inserted with its text is a
                * region that did not exist when the refusal happened. It stands where the trainer's
                * eye already is — beside the button they just pressed. */}
              <p className={styles.footAlert} role="alert">
                {body.invalid.length === 0
                  ? ''
                  : t(`${KEY}.invalidCount`, { count: body.invalid.length })}
              </p>
              <Dialog.Close className={`${String(ghost.ghost)} ${String(styles.cancel)}`}>
                {t('clients.detail.overview.note.cancel')}
              </Dialog.Close>
              {/* Never `disabled` mid-request: a primary that leaves the tab order while the write is
                * in flight takes the keyboard back to the top of the panel. `aria-busy` says what is
                * happening, the label says it in words, and `submit` swallows a repeat press. */}
              <button
                aria-busy={body.saving || undefined}
                className={`${String(ink.ink)} ${String(styles.save)}`}
                type="submit"
              >
                {body.saving ? t(`${KEY}.drawerSaving`) : t('clients.detail.body.save')}
              </button>
            </div>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
